import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Ship, Bell, Globe, LayoutDashboard, LogOut, Menu } from "lucide-react";
import api from "@/lib/api";

export function Navbar({ dark = false, onMenu }) {
  const { t, lang, setLang } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);

  useEffect(() => {
    if (user) {
      api.get("/notifications").then(({ data }) => setNotifs(data)).catch(() => {});
    }
  }, [user]);

  const unread = notifs.filter((n) => !n.read).length;
  const base = dark ? "text-white" : "text-primary";

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`).catch(() => {});
    setNotifs((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  };

  return (
    <header className={`sticky top-0 z-50 w-full border-b ${dark ? "bg-primary/95 border-white/10 backdrop-blur" : "bg-background/90 border-border backdrop-blur"}`}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 flex h-16 items-center justify-between">
        <Link to="/" data-testid="nav-logo" className={`flex items-center gap-2 font-heading font-extrabold text-xl tracking-tight ${base}`}>
          <span className={`grid h-9 w-9 place-items-center ${dark ? "bg-accent" : "bg-primary"} text-white`}>
            <Ship className="h-5 w-5" />
          </span>
          Colis2Go
        </Link>

        <div className="flex items-center gap-1 sm:gap-3">
          <Link to="/how-it-works" data-testid="nav-how" className={`hidden sm:inline text-sm font-medium hover:text-accent transition-colors ${base}`}>
            {t("nav.how")}
          </Link>

          <button
            data-testid="lang-toggle"
            onClick={() => setLang(lang === "fr" ? "en" : "fr")}
            className={`flex items-center gap-1 text-sm font-semibold uppercase px-1.5 sm:px-2 py-1 border ${dark ? "border-white/20 text-white" : "border-border text-primary"} hover:border-accent transition-colors`}
          >
            <Globe className="h-4 w-4" /> {lang}
          </button>

          {user && (
            <Popover>
              <PopoverTrigger asChild>
                <button data-testid="nav-notifications" className={`relative p-2 ${base} hover:text-accent transition-colors`}>
                  <Bell className="h-5 w-5" />
                  {unread > 0 && <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 grid place-items-center bg-accent text-white text-[10px] rounded-full">{unread}</span>}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-0">
                <div className="px-4 py-3 border-b flex items-center justify-between gap-2">
                  <span className="font-heading font-bold">{t("dash.notifications")}</span>
                  {unread > 0 && (
                    <button data-testid="notif-mark-all" className="text-xs font-semibold text-accent hover:underline"
                      onClick={async () => { await api.post("/notifications/read-all").catch(() => {}); setNotifs((prev) => prev.map((n) => ({ ...n, read: true }))); }}>
                      Tout marquer comme lu
                    </button>
                  )}
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notifs.length === 0 && <div className="p-4 text-sm text-muted-foreground">—</div>}
                  {notifs.slice(0, 15).map((n) => (
                    <button key={n.id} onClick={() => markRead(n.id)} className={`w-full text-left px-4 py-3 border-b hover:bg-secondary transition-colors ${n.read ? "opacity-60" : ""}`}>
                      <div className="text-sm font-semibold">{n.title}</div>
                      <div className="text-xs text-muted-foreground">{n.body}</div>
                    </button>
                  ))}
                </div>
                <button data-testid="notif-see-all" className="w-full px-4 py-2.5 text-sm font-semibold text-accent hover:bg-secondary border-t"
                  onClick={() => navigate("/notifications")}>
                  Voir toutes les notifications
                </button>
              </PopoverContent>
            </Popover>
          )}

          {user ? (
            <>
              <Button data-testid="nav-dashboard" variant={dark ? "secondary" : "outline"} size="sm" onClick={() => navigate("/dashboard")} className="rounded-sm">
                <LayoutDashboard className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">{t("nav.dashboard")}</span>
              </Button>
              <Button data-testid="nav-logout" variant="ghost" size="sm" onClick={async () => { await logout(); navigate("/"); }} className={`rounded-sm ${dark ? "text-white hover:text-accent" : ""}`}>
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Button data-testid="nav-login" variant={dark ? "ghost" : "ghost"} size="sm" onClick={() => navigate("/login")} className={`rounded-sm px-2 sm:px-3 ${dark ? "text-white hover:text-accent" : ""}`}>
                {t("nav.login")}
              </Button>
              <Button data-testid="nav-register" size="sm" onClick={() => navigate("/register")} className="rounded-sm px-2 sm:px-3 bg-accent hover:bg-accent/90 text-white">
                {t("nav.register")}
              </Button>
            </>
          )}

          {onMenu && (
            <button data-testid="nav-burger" aria-label="Menu" onClick={onMenu}
              className={`p-1 sm:p-2 ${base} hover:text-accent transition-colors`}>
              <Menu className="h-6 w-6" />
            </button>
          )}
        </div>
      </div>
    </header>
  );
}

export default Navbar;
